import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageShell } from '../components/elevation';
import { RepoMarkdown } from '../components/markdown/RepoMarkdown';
import { linkPullRequests } from '../components/markdown/linkPullRequests';
import roadmapFile from '../../docs/3-system/PRODUCT_ROADMAP.md?raw';
import { withFinishedFiles, withItemsTogether, withMergedDates } from '../lib/roadmapFinishedFiles';

/** Rows of items finished from 2026-10-29 on, one file per ID (lib/roadmapFinishedFiles.ts says why). */
const finishedFiles = Object.entries(
  import.meta.glob('../../docs/3-system/roadmap-finished/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>,
).map(([path, text]) => ({ name: path.split('/').pop() ?? '', text }));
const mergedDates = typeof __ROADMAP_MERGED_DATES__ === 'undefined' ? {} : __ROADMAP_MERGED_DATES__;
const roadmap = withMergedDates(withItemsTogether(withFinishedFiles(roadmapFile, finishedFiles)), mergedDates);

const REPO_FILE = 'https://github.com/brightwes/priorities-ai/blob/main/docs/3-system/PRODUCT_ROADMAP.md';

/** When the file last changed, from git at build time (vite.config.ts); null only outside a build. */
function lastChanged(): string | null {
  const change = typeof __ROADMAP_LAST_CHANGE__ === 'undefined' ? null : __ROADMAP_LAST_CHANGE__;
  if (!change) return null;
  if (change.source === 'git') return `Last changed ${change.date}${change.subject ? ` — ${change.subject}` : ''}`;
  return `As of the build on ${change.date}`;
}

/**
 * The product roadmap, at /home/roadmap: a product record behind the team hub,
 * for the team, never customers (Proposal 09 § 8.2; lib/elevationAccess.ts).
 * The text is docs/3-system/PRODUCT_ROADMAP.md, loaded at
 * build time, with the Finished rows kept one per file in roadmap-finished/
 * added to its table — this page adds nothing else but the date it last changed, which
 * comes from git rather than from a line in the file, and links on its pull
 * request numbers. To change the roadmap, edit the file.
 */
export const Roadmap: React.FC = () => {
  const changed = lastChanged();
  return (
    <PageShell>
      <p className="mb-6 font-mono text-[13px] uppercase tracking-[0.2em] text-[var(--text-ter)]">
        Shown from docs/3-system/PRODUCT_ROADMAP.md ·{' '}
        <a href={REPO_FILE} target="_blank" rel="noopener noreferrer" className="text-[var(--p-gold)] underline underline-offset-4">
          open the file
        </a>
      </p>
      {changed && (
        <p className="mb-6 -mt-4 text-[15px] text-[var(--text-sec)]" data-testid="roadmap-last-changed">
          {changed}
        </p>
      )}
      {/* An ID ("S-12") on one line: the narrow first column broke it at the hyphen. */}
      <div className="[&_td:first-child]:whitespace-nowrap [&_th:first-child]:whitespace-nowrap">
        <RepoMarkdown>{linkPullRequests(roadmap)}</RepoMarkdown>
      </div>
    </PageShell>
  );
};

/**
 * The roadmap's old address, /elevation/roadmap (and the older /roadmap, which
 * the legacy catch-all sends here). Bookmarks and shared links keep working:
 * they land on /home/roadmap with their query and anchor.
 */
export const RoadmapOldAddressRedirect: React.FC = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/home/roadmap${search}${hash}`} replace />;
};
